import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, brl, eletrosMediaUrl } from "../../lib/api";
import { toast } from "sonner";
import { Zap, User, ShoppingBag, Wallet, ClipboardCheck, Search, Plus, Minus, Check, ChevronLeft, ChevronRight, X, AlertTriangle } from "lucide-react";

const PURPLE = "#2B1E4A";
const GOLD = "#F5B400";
const STEPS = [
  { n: 1, label: "Cliente", icon: User },
  { n: 2, label: "Produtos", icon: ShoppingBag },
  { n: 3, label: "Pagamento", icon: Wallet },
  { n: 4, label: "Confirmacao", icon: ClipboardCheck },
];

export default function RevendaEletrosSaleWizard() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const opportunityId = params.get("opportunity_id") || undefined;
  const [step, setStep] = useState(1);
  const [access, setAccess] = useState(null);
  const [products, setProducts] = useState([]);

  const [clientQ, setClientQ] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [client, setClient] = useState(null);
  const [searching, setSearching] = useState(false);

  const [cart, setCart] = useState([]); // {product, qty}
  const [prodQ, setProdQ] = useState("");

  const [method, setMethod] = useState("cash");
  const [installmentsCount, setInstallmentsCount] = useState(1);
  const [firstDueDate, setFirstDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, p] = await Promise.all([api.get("/eletros/me/access"), api.get("/eletros/products")]);
        setAccess(a.data); setProducts(p.data);
      } catch { setAccess({ enabled: false }); }
    })();
  }, []);

  // client search (debounced)
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!clientQ.trim()) { setClientResults([]); return; }
      setSearching(true);
      try { setClientResults((await api.get("/eletros/clients/search", { params: { q: clientQ } })).data); }
      catch { setClientResults([]); }
      finally { setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [clientQ]);

  const filteredProducts = useMemo(() => {
    const s = prodQ.trim().toLowerCase();
    const inStock = products.filter((p) => p.active && p.stock > 0);
    if (!s) return inStock;
    return inStock.filter((p) => [p.name, p.brand, p.model, p.sku, p.barcode].filter(Boolean).some((v) => v.toLowerCase().includes(s)));
  }, [products, prodQ]);

  const total = cart.reduce((a, c) => a + c.qty * Number(c.product.sale_price), 0);
  const commissionPct = Math.round((access?.commission_rate || 0) * 1000) / 10;
  const maxInst = Math.max(1, Number(access?.max_installments || 1));
  const canInstall = !!access?.installments_allowed && maxInst > 1;

  const addToCart = (product) => {
    setCart((prev) => {
      const i = prev.findIndex((c) => c.product.id === product.id);
      if (i >= 0) {
        if (prev[i].qty >= product.stock) { toast.error("Estoque insuficiente"); return prev; }
        const next = [...prev]; next[i] = { ...next[i], qty: next[i].qty + 1 }; return next;
      }
      return [...prev, { product, qty: 1 }];
    });
  };
  const changeQty = (id, d) => setCart((prev) => prev.flatMap((c) => {
    if (c.product.id !== id) return [c];
    const q = c.qty + d;
    if (q <= 0) return [];
    if (q > c.product.stock) { toast.error("Estoque insuficiente"); return [c]; }
    return [{ ...c, qty: q }];
  }));

  const goNext = () => {
    if (step === 1 && !client) { toast.error("Selecione uma cliente"); return; }
    if (step === 2 && cart.length === 0) { toast.error("Adicione ao menos um produto"); return; }
    setStep((s) => Math.min(4, s + 1)); window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const createSale = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        client_id: client.id,
        items: cart.map((c) => ({ product_id: c.product.id, qty: c.qty })),
        payment_method: method,
        installments_count: method === "installment" ? Number(installmentsCount) : 1,
        first_due_date: method === "installment" && firstDueDate ? firstDueDate : undefined,
        opportunity_id: opportunityId,
      };
      const { data } = await api.post("/eletros/sales", payload);
      toast.success(opportunityId ? "Venda Eletros registrada! Oportunidade convertida." : "Venda Eletros registrada!");
      nav(`/revenda/eletros`, { state: { created: data.id } });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao registrar venda"); }
    finally { setSubmitting(false); setConfirm(false); }
  };

  if (!access) return <div className="p-6 text-sm text-[#5C5A57]">Carregando...</div>;
  if (!access.enabled) return (
    <div className="grid min-h-[50vh] place-items-center p-6" data-testid="eletros-wizard-locked">
      <div className="max-w-sm rounded-2xl border border-border bg-white p-8 text-center">
        <AlertTriangle size={26} className="mx-auto mb-3 text-[#5B2CA0]" />
        <h2 className="font-display text-lg font-bold text-brand-dark">Eletros nao habilitado</h2>
        <p className="mt-2 text-sm text-[#6B5A69]">Fale com o administrador para habilitar sua operacao Eletros.</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-5 p-4 lg:p-8" data-testid="eletros-sale-wizard">
      <div className="flex items-center gap-3 rounded-2xl p-4 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, #5B2CA0)` }}>
        <div className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: GOLD }}><Zap size={22} className="text-[#2B1E4A]" /></div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>Eletros</div>
          <h1 className="font-display text-lg font-bold">Nova venda Eletros</h1>
        </div>
      </div>

      {/* Steps */}
      <div className="rounded-2xl border border-border bg-white p-4" data-testid="eletros-step-indicator">
        <div className="flex items-center">
          {STEPS.map((s, idx) => {
            const Icon = s.icon; const done = step > s.n; const active = step === s.n;
            return (
              <React.Fragment key={s.n}>
                <div className="flex flex-1 flex-col items-center gap-1 sm:flex-row sm:flex-none sm:gap-2">
                  <span className={`grid h-8 w-8 place-items-center rounded-full text-sm font-semibold ${active ? "text-white" : done ? "text-white" : "bg-muted text-[#9A8A98]"}`} style={active || done ? { background: PURPLE } : {}}>
                    {done ? <Check size={15} /> : <Icon size={15} />}
                  </span>
                  <span className={`text-[11px] font-semibold ${active || done ? "text-brand-dark" : "text-[#9A8A98]"}`}>{s.label}</span>
                </div>
                {idx < STEPS.length - 1 && <div className={`mx-2 hidden h-0.5 flex-1 rounded sm:block ${step > s.n ? "bg-[#5B2CA0]" : "bg-muted"}`} />}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* STEP 1 - Cliente */}
      {step === 1 && (
        <section className="rounded-2xl border border-border bg-white p-5" data-testid="eletros-step-client">
          <h2 className="mb-3 font-display text-base font-bold text-brand-dark">Selecione a cliente</h2>
          <div className="flex items-center gap-2 rounded-xl border border-border px-3 py-2">
            <Search size={16} className="text-[#9A8A98]" />
            <input value={clientQ} onChange={(e) => setClientQ(e.target.value)} placeholder="Buscar por nome, CPF ou telefone" className="w-full text-sm outline-none" data-testid="eletros-client-search" />
          </div>
          {searching && <div className="mt-2 text-[11px] text-[#5B2CA0]">Buscando...</div>}
          <div className="mt-3 space-y-2">
            {clientResults.map((c) => (
              <button key={c.id} onClick={() => setClient(c)} data-testid={`eletros-client-opt-${c.id}`}
                className={`flex w-full items-center justify-between rounded-xl border p-3 text-left ${client?.id === c.id ? "border-[#5B2CA0] bg-[#F1ECF7]" : "border-border hover:bg-muted/50"}`}>
                <div className="min-w-0">
                  <div className="truncate font-medium text-brand-dark">{c.name}</div>
                  <div className="truncate text-[12px] text-[#8A7788]">{c.cpf || "sem CPF"} · {c.phone || "sem telefone"}</div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] text-[#8A7788]">Score {c.score}</div>
                  {c.overdue_count > 0 && <div className="text-[11px] font-semibold text-red-500">{c.overdue_count} em atraso</div>}
                </div>
              </button>
            ))}
            {clientQ && !searching && clientResults.length === 0 && <div className="py-4 text-center text-sm text-[#9A8A98]">Nenhuma cliente encontrada.</div>}
          </div>
          {client && (
            <div className="mt-3 flex items-center justify-between rounded-xl bg-[#F1ECF7] p-3" data-testid="eletros-client-selected">
              <div><div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Selecionada</div><div className="font-semibold text-brand-dark">{client.name}</div></div>
              <button onClick={() => setClient(null)} className="grid h-7 w-7 place-items-center rounded-lg bg-white"><X size={15} /></button>
            </div>
          )}
        </section>
      )}

      {/* STEP 2 - Produtos */}
      {step === 2 && (
        <section className="rounded-2xl border border-border bg-white p-5" data-testid="eletros-step-products">
          <h2 className="mb-3 font-display text-base font-bold text-brand-dark">Produtos</h2>
          <div className="mb-3 flex items-center gap-2 rounded-xl border border-border px-3 py-2">
            <Search size={16} className="text-[#9A8A98]" />
            <input value={prodQ} onChange={(e) => setProdQ(e.target.value)} placeholder="Buscar produto" className="w-full text-sm outline-none" data-testid="eletros-product-search" />
          </div>
          <div className="grid max-h-[46vh] grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
            {filteredProducts.map((p) => (
              <button key={p.id} onClick={() => addToCart(p)} data-testid={`eletros-add-${p.id}`} className="flex items-center gap-3 rounded-xl border border-border p-2 text-left hover:border-[#5B2CA0]">
                <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg bg-[#F1ECF7]">
                  {p.cover_image ? <img src={eletrosMediaUrl(p.cover_image)} alt="" className="h-full w-full object-cover" /> : <Zap size={18} className="text-[#B7A6C9]" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-brand-dark">{p.name}</div>
                  <div className="text-[11px] text-[#8A7788]">Estoque {p.stock}</div>
                  <div className="font-display text-sm font-bold" style={{ color: PURPLE }}>{brl(p.sale_price)}</div>
                </div>
                <Plus size={16} className="text-[#5B2CA0]" />
              </button>
            ))}
            {filteredProducts.length === 0 && <div className="col-span-2 py-6 text-center text-sm text-[#9A8A98]">Nenhum produto disponivel.</div>}
          </div>

          <div className="mt-4 border-t border-border pt-3">
            <div className="mb-2 text-[10px] uppercase tracking-widest text-[#9A8A98]">Carrinho ({cart.length})</div>
            {cart.length === 0 ? <div className="py-3 text-center text-sm text-[#9A8A98]">Nenhum item adicionado.</div> : (
              <div className="space-y-2" data-testid="eletros-cart">
                {cart.map((c) => (
                  <div key={c.product.id} className="flex items-center justify-between gap-3 rounded-xl bg-muted/50 p-2" data-testid={`eletros-cart-item-${c.product.id}`}>
                    <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium text-brand-dark">{c.product.name}</div><div className="text-[11px] text-[#8A7788]">{brl(c.product.sale_price)} un.</div></div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => changeQty(c.product.id, -1)} className="grid h-7 w-7 place-items-center rounded-lg border border-border" data-testid={`eletros-qty-minus-${c.product.id}`}><Minus size={14} /></button>
                      <span className="w-6 text-center text-sm font-semibold" data-testid={`eletros-qty-${c.product.id}`}>{c.qty}</span>
                      <button onClick={() => changeQty(c.product.id, 1)} className="grid h-7 w-7 place-items-center rounded-lg border border-border" data-testid={`eletros-qty-plus-${c.product.id}`}><Plus size={14} /></button>
                    </div>
                    <div className="w-20 text-right text-sm font-bold text-brand-dark">{brl(c.qty * c.product.sale_price)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* STEP 3 - Pagamento */}
      {step === 3 && (
        <section className="rounded-2xl border border-border bg-white p-5" data-testid="eletros-step-payment">
          <h2 className="mb-3 font-display text-base font-bold text-brand-dark">Forma de pagamento</h2>
          <div className="grid grid-cols-2 gap-3">
            <button onClick={() => setMethod("cash")} data-testid="eletros-method-cash" className={`rounded-xl border p-4 text-left ${method === "cash" ? "border-[#5B2CA0] bg-[#F1ECF7]" : "border-border"}`}>
              <Wallet size={18} className="text-[#5B2CA0]" /><div className="mt-1 font-semibold text-brand-dark">A vista</div><div className="text-[11px] text-[#8A7788]">Pagamento unico</div>
            </button>
            <button onClick={() => canInstall && setMethod("installment")} disabled={!canInstall} data-testid="eletros-method-installment" className={`rounded-xl border p-4 text-left disabled:opacity-40 ${method === "installment" ? "border-[#5B2CA0] bg-[#F1ECF7]" : "border-border"}`}>
              <ClipboardCheck size={18} className="text-[#5B2CA0]" /><div className="mt-1 font-semibold text-brand-dark">Parcelado</div><div className="text-[11px] text-[#8A7788]">{canInstall ? `Ate ${maxInst}x` : "Nao permitido"}</div>
            </button>
          </div>
          {method === "installment" && (
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Numero de parcelas</label>
                <select value={installmentsCount} onChange={(e) => setInstallmentsCount(e.target.value)} className="inp" data-testid="eletros-installments-select">
                  {Array.from({ length: maxInst }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{`${n}x de ${brl(total / n)}`}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[10px] uppercase tracking-widest text-[#9A8A98]">Data do 1o vencimento</label>
                <input type="date" value={firstDueDate} min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setFirstDueDate(e.target.value)} className="inp" data-testid="eletros-first-due-date" />
              </div>
              <DueCalendar total={total} n={Number(installmentsCount)} firstDue={firstDueDate} />
            </div>
          )}
          <div className="mt-4 rounded-xl bg-muted/50 p-3 text-sm">
            <div className="flex justify-between"><span className="text-[#8A7788]">Total da venda</span><span className="font-bold text-brand-dark">{brl(total)}</span></div>
            <div className="flex justify-between"><span className="text-[#8A7788]">Comissao vigente</span><span className="font-semibold" style={{ color: "#C9960A" }}>{commissionPct}%</span></div>
          </div>
        </section>
      )}

      {/* STEP 4 - Confirmacao */}
      {step === 4 && (
        <section className="rounded-2xl border border-border bg-white p-5" data-testid="eletros-step-confirm">
          <h2 className="mb-3 font-display text-base font-bold text-brand-dark">Confirmacao</h2>
          <div className="mb-3 flex justify-between rounded-xl bg-muted/50 p-3 text-sm">
            <div><div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Cliente</div><div className="font-semibold text-brand-dark">{client?.name}</div></div>
            <div className="text-right"><div className="text-[10px] uppercase tracking-widest text-[#9A8A98]">Pagamento</div><div className="font-semibold text-brand-dark">{method === "cash" ? "A vista" : `${installmentsCount}x`}</div></div>
          </div>
          <div className="space-y-1.5">
            {cart.map((c) => (
              <div key={c.product.id} className="flex justify-between text-sm" data-testid={`eletros-confirm-item-${c.product.id}`}>
                <span className="min-w-0 truncate text-brand-dark">{c.qty}x {c.product.name}</span>
                <span className="ml-3 shrink-0 font-medium text-brand-dark">{brl(c.qty * c.product.sale_price)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-between border-t border-border pt-3">
            <span className="font-semibold text-brand-dark">Total</span>
            <span className="font-display text-lg font-bold" style={{ color: PURPLE }} data-testid="eletros-confirm-total">{brl(total)}</span>
          </div>
        </section>
      )}

      {/* Nav */}
      <div className="flex items-center justify-between gap-3">
        {step > 1 ? <button onClick={goBack} className="flex items-center gap-1 rounded-xl border border-border px-4 py-2.5 text-sm font-semibold text-[#6B5A69]" data-testid="eletros-step-back"><ChevronLeft size={16} /> Voltar</button> : <span />}
        {step < 4 ? (
          <button onClick={goNext} className="flex items-center gap-1 rounded-xl px-5 py-2.5 text-sm font-semibold text-white" style={{ background: PURPLE }} data-testid="eletros-step-next">Continuar <ChevronRight size={16} /></button>
        ) : (
          <button onClick={() => setConfirm(true)} disabled={submitting || cart.length === 0} className="flex items-center gap-1 rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60" style={{ background: PURPLE }} data-testid="eletros-submit-sale"><Check size={16} /> Registrar venda</button>
        )}
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-6" onClick={() => !submitting && setConfirm(false)}>
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 shadow-pop" onClick={(e) => e.stopPropagation()} data-testid="eletros-confirm-modal">
            <div className="text-center"><div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl" style={{ background: "#F1ECF7" }}><ClipboardCheck size={24} className="text-[#5B2CA0]" /></div>
              <h3 className="font-bold text-brand-dark">Confirmar venda Eletros?</h3>
              <p className="mt-1 text-sm text-[#6B5A69]">{client?.name} · {brl(total)} · {method === "cash" ? "a vista" : `${installmentsCount}x`}</p>
            </div>
            <div className="mt-5 flex flex-col gap-2">
              <button onClick={createSale} disabled={submitting} className="rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-60" style={{ background: PURPLE }} data-testid="eletros-confirm-final">{submitting ? "Registrando..." : "Confirmar"}</button>
              <button onClick={() => setConfirm(false)} disabled={submitting} className="rounded-xl py-2.5 text-xs font-semibold uppercase tracking-widest text-[#6B5A69]">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function addMonthsClamp(base, n) {
  const y = base.getFullYear();
  const m = base.getMonth() + n;
  const targetY = y + Math.floor(m / 12);
  const targetM = ((m % 12) + 12) % 12;
  const lastDay = new Date(targetY, targetM + 1, 0).getDate();
  return new Date(targetY, targetM, Math.min(base.getDate(), lastDay));
}

// Calendario/resumo das parcelas exibido ANTES da confirmacao (espelha o backend: mensal + clamp de dia).
function DueCalendar({ total, n, firstDue }) {
  if (!firstDue || !n || n < 1) {
    return <div className="rounded-xl border border-dashed border-[#D9CFE8] p-3 text-[12px] text-[#8A7788]" data-testid="eletros-due-calendar-empty">Selecione a data do 1o vencimento para ver o calendario das parcelas.</div>;
  }
  const base = new Date(firstDue + "T00:00:00");
  const val = total / n;
  const rows = Array.from({ length: n }, (_, i) => {
    const d = addMonthsClamp(base, i);
    return { num: i + 1, date: d.toLocaleDateString("pt-BR") };
  });
  return (
    <div className="rounded-xl border border-border p-3" data-testid="eletros-due-calendar">
      <div className="mb-2 text-[10px] uppercase tracking-widest text-[#9A8A98]">Calendario das parcelas</div>
      <ul className="space-y-1">
        {rows.map((r) => (
          <li key={r.num} className="flex justify-between text-sm">
            <span className="text-[#6B5A69]">{r.num}/{n} · {r.date}</span>
            <span className="font-semibold text-brand-dark">R$ {val.toFixed(2).replace(".", ",")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
